import { http } from '@/api/http';

/** Book catalogue, favourites, language and theme preferences. */

export interface Author {
    id: number;
    full_name: string;
    /**
     * How many books the author holds under the filters being browsed with.
     * Only the author search reports it — a book listing its own authors does
     * not, so it is absent far more often than it is present.
     */
    books_count?: number;
}

/**
 * AuthorHit is an author as the author search and the author heading name
 * one: a catalogue author, or under the entity identity an author entity —
 * its UUID, and a label when another entity has the same name.
 */
export interface AuthorHit {
    id: number | string;
    full_name: string;
    /** A namesake's label: its dominant genre, or else (label_books) its book count. */
    label?: string;
    label_books?: number;
    /** How many books the author holds under the filters being browsed with. */
    books_count?: number;
}

export interface Genre {
    id: number;
    genre: string;
    section?: string;
}

export interface Series {
    id: number;
    ser: string;
    ser_no: number;
}

/**
 * Book mirrors models.Book on the backend.
 *
 * Note `cover` is a boolean — whether the book has one — not a URL. Components
 * used to declare it as a string locally, which nothing read, so the mistake
 * stayed invisible.
 */
export interface Book {
    id: number;
    title: string;
    authors: Author[];
    series: Series[];
    genres: Genre[];
    annotation: string;
    filename: string;
    cover: boolean;
    registerdate: string;
    docdate: string;
    lang: string;
    fav: boolean;
    approved: boolean;
    path: string;
    format: string;
    favorite_count: number;
    md5?: string;
    duplicate_hidden?: boolean;
    position?: number;
    /**
     * From the book's current metadata snapshot: null and [] when it has none.
     * Only the book list sends them, so they are optional here.
     */
    publisher?: string | null;
    isbn?: string[];
    /**
     * The book's author line, sent only while the server shows authors from
     * the author layer: the names in the file's order, each with the
     * catalogue author it links to when there is one. Absent, the card shows
     * `authors`.
     */
    authors_display?: AuthorDisplay[];
}

/** One name of a book's author line. */
export interface AuthorDisplay {
    name: string;
    /** The catalogue author the name links to; absent when it is none of them. */
    legacy_author_id?: number;
    /**
     * Under the entity identity, the author entities the name links to
     * instead: one, or every person of a name of several. Absent when it
     * links to none.
     */
    entities?: AuthorEntityRef[];
}

/** An author entity a name links to, as its page names it. */
export interface AuthorEntityRef {
    id: string;
    full_name: string;
    label?: string;
    label_books?: number;
}

export interface BooksPage {
    books: Book[];
    length: number;
}

/**
 * What the server actually sends for a page: a nil Go slice marshals to JSON
 * null, so a zero-hit answer carries "books": null. Kept separate from
 * BooksPage so the wire stays honest while consumers are promised an array.
 */
interface BooksPageWire {
    books: Book[] | null;
    length: number;
}

/** BooksQuery mirrors models.BookFilters on the backend, field for field. */
export interface BooksQuery {
    limit?: number;
    offset?: number;
    title?: string;
    author?: number | string;
    series?: number | string;
    genre?: number | string;
    lang?: string;
    fav?: boolean;
    unapproved?: boolean;
    users_favorites?: boolean;
    collection?: number | string;
    curated_collection?: number | string;
    include_hidden?: boolean;
    /** Pins the result to one exact book — the pick an autocomplete offered. */
    book_id?: number | string;
}

export interface Language {
    lang: string;
    language_count: number;
}

export type ThemeMode = 'light' | 'dark';

export const listBooks = async (query: BooksQuery): Promise<BooksPage> => {
    const data = await http.get<BooksPageWire>('/books/list', {
        query: query as Record<string, string | number | boolean | undefined>,
    });
    // Normalise once at the boundary so every consumer gets an array even
    // when the server answers a zero-hit search with "books": null.
    return { ...data, books: data.books ?? [] };
};

export const listAuthors = (query: {
    author?: string;
    limit?: number;
    offset?: number;
    /**
     * The reader's books language, which has to be the one the book list uses:
     * the count each author is offered with is a count under this filter, and
     * the two disagreeing would make it a promise the next page breaks.
     */
    lang?: string;
}) => http.get<{ authors: AuthorHit[]; length: number }>('/books/authors', { query });

/**
 * getAuthor names a single author, which the search panel needs when a reader
 * arrives on an author's list by URL rather than by following a link — there is
 * no name in `/books/find/author/42/1` to put on screen. Under the entity
 * identity the id is the entity's UUID and the answer carries its label.
 */
export const getAuthor = (id: number | string) =>
    http.post<AuthorHit>('/books/author', {
        // A legacy author goes as the number it is, an entity as its UUID.
        author_id: /^\d+$/.test(String(id)) ? Number(id) : String(id),
    });

export const listLanguages = () => http.get<{ langs: Language[] }>('/books/langs');

/** toggleFavourite adds or removes a book from the caller's favourites. */
export const toggleFavourite = (bookID: number, fav: boolean) =>
    http.post<{ have_favs: boolean }>('/books/fav', { book_id: bookID, fav });

export const getThemePreference = () => http.get<{ theme?: ThemeMode }>('/books/theme');

export const setThemePreference = (theme: ThemeMode) => http.post<void>('/books/theme', { theme });
